import React from "react";
import SettingsSection from "../SettingsSection";
import ChannelPicker from "../ChannelPicker";
import RolePicker from "../RolePicker";

const PLATFORMS = [
  { value: "twitch", label: "Twitch" },
  { value: "youtube", label: "YouTube" },
  { value: "kick", label: "Kick (manual only)" },
];

function newCreator() {
  return {
    id: crypto.randomUUID(),
    platform: "twitch",
    platformId: "",
    displayName: "",
    discordUserId: "",
  };
}

export default function StreamAlertsSection({
  channels,
  roles,
  streamAlerts,
  setStreamAlerts,
}) {
  const update = (patch) => setStreamAlerts((prev) => ({ ...prev, ...patch }));

  const updateCreator = (id, patch) => {
    setStreamAlerts((prev) => ({
      ...prev,
      creators: prev.creators.map((c) =>
        c.id === id ? { ...c, ...patch } : c,
      ),
    }));
  };

  const removeCreator = (id) => {
    setStreamAlerts((prev) => ({
      ...prev,
      creators: prev.creators.filter((c) => c.id !== id),
    }));
  };

  return (
    <SettingsSection>
      <p className="help-text" style={{ marginBottom: "15px" }}>
        Ping a channel when you go live on Twitch or YouTube. Auto detection works
        out of the box - no server setup needed. Optional API keys in the bot{" "}
        <code>.env</code> (Twitch Client ID + Secret, YouTube API key) improve
        reliability. YouTube accepts <code>@handle</code> or a <code>UC...</code>{" "}
        channel ID. Use <code>/admin stream status</code> to debug.
      </p>

      <label
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          cursor: "pointer",
          marginBottom: "20px",
        }}
      >
        <input
          type="checkbox"
          checked={streamAlerts.enabled}
          onChange={(e) => update({ enabled: e.target.checked })}
        />
        <span style={{ fontSize: "0.9rem", fontWeight: "bold" }}>
          ENABLE STREAM ALERTS
        </span>
      </label>

      <label className="input-label">ANNOUNCEMENT CHANNEL</label>
      <ChannelPicker
        channels={channels}
        value={streamAlerts.channelId}
        onChange={(v) => update({ channelId: v })}
        emptyLabel="None"
      />

      <label className="input-label" style={{ marginTop: "15px" }}>
        PING ROLE (OPTIONAL)
      </label>
      <RolePicker
        roles={roles}
        value={streamAlerts.pingRoleId}
        onChange={(v) => update({ pingRoleId: v })}
        emptyLabel="None"
        placeholder="Select ping role…"
      />

      <label
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          cursor: "pointer",
          margin: "16px 0",
        }}
      >
        <input
          type="checkbox"
          checked={streamAlerts.mentionEveryone}
          onChange={(e) => update({ mentionEveryone: e.target.checked })}
        />
        <span style={{ fontSize: "0.85rem" }}>Ping @everyone (use carefully)</span>
      </label>

      <label
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          cursor: "pointer",
          margin: "0 0 16px",
        }}
      >
        <input
          type="checkbox"
          checked={Boolean(streamAlerts.combineMultiPlatform)}
          onChange={(e) => update({ combineMultiPlatform: e.target.checked })}
        />
        <span style={{ fontSize: "0.85rem" }}>
          Combine multi-platform alerts into one message
        </span>
      </label>
      <p
        className="help-text"
        style={{ marginTop: "-8px", marginBottom: "16px", opacity: 0.65 }}
      >
        When on, Twitch + YouTube (same Discord user ID or matching display name)
        share a single go-live post. If another platform goes live later, that
        message is updated instead of posting again.
      </p>

      <label className="input-label">ANNOUNCEMENT MESSAGE</label>
      <textarea
        value={streamAlerts.message}
        onChange={(e) => update({ message: e.target.value })}
        className="settings-input large-text"
        rows={4}
      />
      <p className="help-text" style={{ marginTop: "8px" }}>
        Placeholders: {"{streamer}"} {"{platform}"} {"{title}"} {"{url}"}{" "}
        {"{game}"} {"{viewers}"} {"{user}"}
      </p>

      <label className="input-label" style={{ marginTop: "20px" }}>
        TRACKED CHANNELS
      </label>
      <p className="help-text" style={{ marginBottom: "10px" }}>
        Fill in the Twitch login or YouTube handle for each row before saving.
        Empty rows are not saved.
      </p>
      {streamAlerts.creators.length === 0 ? (
        <p className="help-text">No channels tracked yet.</p>
      ) : (
        streamAlerts.creators.map((creator) => (
          <div
            key={creator.id}
            style={{
              border: "1px solid rgba(74, 14, 23, 0.25)",
              borderRadius: "10px",
              padding: "14px",
              marginBottom: "12px",
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "10px",
              }}
            >
              <div>
                <label className="input-label">PLATFORM</label>
                <select
                  value={creator.platform}
                  onChange={(e) =>
                    updateCreator(creator.id, { platform: e.target.value })
                  }
                  className="settings-input"
                  style={{ marginBottom: "8px" }}
                >
                  {PLATFORMS.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="input-label">CHANNEL ID / USERNAME</label>
                <input
                  type="text"
                  value={creator.platformId}
                  onChange={(e) =>
                    updateCreator(creator.id, { platformId: e.target.value })
                  }
                  className="settings-input"
                  placeholder="twitch login, @handle, or UC... channel ID"
                  style={{ marginBottom: "8px" }}
                />
              </div>
            </div>
            <label className="input-label">DISPLAY NAME</label>
            <input
              type="text"
              value={creator.displayName}
              onChange={(e) =>
                updateCreator(creator.id, { displayName: e.target.value })
              }
              className="settings-input"
              placeholder="Shown in the alert"
              style={{ marginBottom: "8px" }}
            />
            <label className="input-label">DISCORD USER ID (OPTIONAL)</label>
            <input
              type="text"
              value={creator.discordUserId}
              onChange={(e) =>
                updateCreator(creator.id, { discordUserId: e.target.value })
              }
              className="settings-input"
              placeholder="For @mention in alerts"
              style={{ marginBottom: "8px" }}
            />
            <button
              type="button"
              className="manage-btn invite-btn"
              style={{ width: "auto", padding: "8px 14px", marginTop: "4px" }}
              onClick={() => removeCreator(creator.id)}
            >
              Remove
            </button>
          </div>
        ))
      )}

      <button
        type="button"
        className="manage-btn"
        style={{ width: "auto", padding: "10px 16px", marginTop: "8px" }}
        onClick={() =>
          update({ creators: [...streamAlerts.creators, newCreator()] })
        }
      >
        Add channel
      </button>
    </SettingsSection>
  );
}
